import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import { MdOutlineSlowMotionVideo } from "react-icons/md";
import ScriptDetailModal from "../Shared/ScriptDetailModal";
import { fetchScripts, deleteScript, generateVideo } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { clearTokens } from "../../utils/auth";
import { API_ENDPOINTS } from "../../config/api";

function ScriptsTable() {
  const [searchText, setSearchText] = useState("");
  const [scripts, setScripts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalScripts, setTotalScripts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [sortCriteria, setSortCriteria] = useState({
    type: "date",
    value: "Newest",
  });

  // State for the detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState(null);

  // New state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // New state for batch operations
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);
  const [batchVideoGenDialogOpen, setBatchVideoGenDialogOpen] = useState(false);
  const [batchVideoGenLoading, setBatchVideoGenLoading] = useState(false);

  // Default video generation options
  const defaultVideoOptions = {
    avatar_id: "Gala_standing_businesssofa_front_close",
    voice_id: "b97d1cce77094bc596d7a5e8c06daddb",
    orientation: "portrait",
    speed: 1.0,
  };

  // Video options state for batch generation
  const [batchVideoOptions, setBatchVideoOptions] = useState({
    avatar_id: "Gala_standing_businesssofa_front_close",
    voice_id: "b97d1cce77094bc596d7a5e8c06daddb",
    orientation: "portrait",
    speed: 1.0,
  });

  // Available avatars and voices
  const AVATARS = [
    { id: "Gala_standing_businesssofa_front_close", name: "Gala (Business)" },
    // { id: "Anna_halflength_brightOffice_front", name: "Anna (Office)" },
    // { id: "Sam_standing_studioLighting_front_far", name: "Sam (Studio)" },
    // { id: "Daniel_halflength_darkGradient_front", name: "Daniel (Dark)" },
  ];

  const VOICES = [
    { id: "b97d1cce77094bc596d7a5e8c06daddb", name: "Emma (Female)" },
    // { id: "455af2320be94075aad4e9350c181c8d", name: "John (Male)" },
    // { id: "f1c76270c17b4aeda12a95110d619a2c", name: "Lisa (Female)" },
    // { id: "c9a97a94384c4a7d96a3d42ef8570bc6", name: "Mark (Male)" },
  ];

  const navigate = useNavigate();

  // Debounce utility function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Function to handle authentication errors
  const handleAuthError = (error) => {
    if (
      error.message &&
      (error.message.includes("Authentication required") ||
        error.message.includes("Invalid token") ||
        error.message.includes("Token expired") ||
        error.message.toLowerCase().includes("unauthorized") ||
        error.message.toLowerCase().includes("authentication"))
    ) {
      // Clear tokens and redirect to login
      clearTokens();
      setNotification({
        open: true,
        message: "Your session has expired. Please log in again.",
        severity: "error",
      });
      // Redirect to login after a short delay
      setTimeout(() => navigate("/login"), 2000);
    }
  };

  // Function to load scripts from the API
  const loadScripts = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);

    try {
      // Get token from localStorage or environment variable
      const accessToken = localStorage.getItem("accessToken");
      const token = accessToken || process.env.REACT_APP_API_TOKEN;

      // Check if a token is available
      if (!token) {
        throw new Error("Authentication required. Please log in.");
      }

      // Construct URL with order_by=-id parameter for latest first
      let url = `${API_ENDPOINTS.SCRIPTS}?order_by=-id&page=${page}`;

      // Add search parameter if provided
      if (search) {
        url += `&name=${encodeURIComponent(search)}`;
      }

      // console.log("Making API request to:", url);

      // Make direct fetch call to ensure proper parameters
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match our component's expected structure
      const transformedScripts = data.results.map((script) => ({
        id: script.id,
        headline: script.name || "Untitled Script",
        date: formatDate(script.created_at),
        originalData: script, // Keep the original data for reference
      }));

      setScripts(transformedScripts);
      setTotalScripts(data.total_items);
      setTotalPages(data.last_page);
      setCurrentPage(data.current_page);

      if (page === 1 && !search) {
        setNotification({
          open: true,
          message: `Successfully fetched ${data.total_items} scripts!`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Error loading scripts:", err);

      // Handle authentication errors
      handleAuthError(err);

      setError("Failed to load scripts. Please try again.");
      setNotification({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedLoadScripts = useCallback(
    debounce((page, search) => {
      loadScripts(page, search);
    }, 500),
    []
  );

  // Format date from API
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Load initial data
  useEffect(() => {
    loadScripts(currentPage, searchText);
  }, [currentPage]);

  // Search change handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    // If search is empty, reset to first page and fetch all scripts
    if (value === "") {
      setCurrentPage(1);
      loadScripts(1);
    } else {
      // Use debounced search to reduce API calls
      debouncedLoadScripts(1, value);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchText("");
    setCurrentPage(1);
    loadScripts(1);
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      loadScripts(1, searchText);
    }
  };

  const handleSortChange = (sortOption) => {
    setSortCriteria(sortOption);

    // When sort criteria changes, reload from page 1
    // Note: We always sort by latest (-id) regardless of the UI selection
    setCurrentPage(1);
    loadScripts(1, searchText);
  };

  // Handler for opening the detail modal
  const handleOpenDetailModal = (script) => {
    // Make sure we have a valid script ID
    if (script && script.id) {
      setSelectedScriptId(script.id);
      setDetailModalOpen(true);
    } else {
      console.error(
        "Cannot open modal: Invalid script object or missing ID",
        script
      );
      // Show an error notification
      setNotification({
        open: true,
        message: "Error: Cannot open script details",
        severity: "error",
      });
    }
  };

  // Handler for closing the detail modal
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedScriptId(null);
  };

  // New handler for opening delete confirmation dialog
  const handleOpenDeleteDialog = (script) => {
    setScriptToDelete(script);
    setDeleteDialogOpen(true);
  };

  // New handler for closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setScriptToDelete(null);
  };

  // New handler for confirming and executing delete
  const handleConfirmDelete = async () => {
    if (!scriptToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteScript(scriptToDelete.id);

      // Close dialog and show success notification
      setDeleteDialogOpen(false);
      setNotification({
        open: true,
        message: "Script deleted successfully",
        severity: "success",
      });

      // Refresh the scripts list
      loadScripts(currentPage, searchText);
    } catch (error) {
      setNotification({
        open: true,
        message: "Error deleting script: " + error.message,
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
      setScriptToDelete(null);
    }
  };

  // Handler for fetching new scripts
  const handleFetchNewScripts = () => {
    setCurrentPage(1); // Reset to first page
    loadScripts(1); // Load fresh data
  };

  // Close notification
  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  // Handle copy link functionality
  const handleCopyLink = (scriptId) => {
    const baseUrl = window.location.origin;
    const scriptUrl = `${baseUrl}/scripts/${scriptId}`;

    navigator.clipboard
      .writeText(scriptUrl)
      .then(() => {
        setNotification({
          open: true,
          message: "Script link copied to clipboard!",
          severity: "success",
        });
      })
      .catch((err) => {
        setNotification({
          open: true,
          message: "Failed to copy link: " + err,
          severity: "error",
        });
      });
  };

  // Handle script selection
  const handleScriptSelect = (scriptId) => {
    setSelectedScripts((prev) => {
      if (prev.includes(scriptId)) {
        return prev.filter((id) => id !== scriptId);
      } else {
        return [...prev, scriptId];
      }
    });
  };

  // Handle select all scripts
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedScripts(scripts.map((script) => script.id));
    } else {
      setSelectedScripts([]);
    }
  };

  // New handler for opening batch delete confirmation dialog
  const handleOpenBatchDeleteDialog = () => {
    if (selectedScripts.length === 0) {
      setNotification({
        open: true,
        message: "Please select at least one script to delete",
        severity: "warning",
      });
      return;
    }
    setBatchDeleteDialogOpen(true);
  };

  // New handler for closing batch delete confirmation dialog
  const handleCloseBatchDeleteDialog = () => {
    setBatchDeleteDialogOpen(false);
  };

  // New handler for confirming and executing batch delete
  const handleConfirmBatchDelete = async () => {
    setBatchDeleteLoading(true);
    const errors = [];
    const successes = [];

    try {
      // Delete all selected scripts sequentially
      for (const scriptId of selectedScripts) {
        try {
          await deleteScript(scriptId);
          successes.push(scriptId);
        } catch (error) {
          console.error(`Error deleting script ID ${scriptId}:`, error);
          errors.push(scriptId);
        }
      }

      // Close dialog
      setBatchDeleteDialogOpen(false);

      // Show success/error notifications
      if (successes.length > 0) {
        setNotification({
          open: true,
          message: `Successfully deleted ${successes.length} script${
            successes.length > 1 ? "s" : ""
          }`,
          severity: "success",
        });
      }

      if (errors.length > 0) {
        setNotification({
          open: true,
          message: `Failed to delete ${errors.length} script${
            errors.length > 1 ? "s" : ""
          }`,
          severity: "error",
        });
      }

      // Clear selections and reload scripts
      setSelectedScripts([]);
      loadScripts(currentPage, searchText);
    } catch (error) {
      setNotification({
        open: true,
        message: "An error occurred while deleting scripts",
        severity: "error",
      });
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  // New handler for opening batch video generation dialog
  const handleOpenBatchVideoGenDialog = () => {
    if (selectedScripts.length === 0) {
      setNotification({
        open: true,
        message: "Please select at least one script to generate videos",
        severity: "warning",
      });
      return;
    }
    setBatchVideoGenDialogOpen(true);
  };

  // New handler for closing batch video generation dialog
  const handleCloseBatchVideoGenDialog = () => {
    setBatchVideoGenDialogOpen(false);
  };

  // New handler for video options change
  const handleBatchVideoOptionChange = (e) => {
    const { name, value } = e.target;
    setBatchVideoOptions({
      ...batchVideoOptions,
      [name]: name === "speed" ? parseFloat(value) : value,
    });
  };

  // Update confirm batch video gen to use selected options
  const handleConfirmBatchVideoGen = async () => {
    setBatchVideoGenLoading(true);
    const errors = [];
    const successes = [];
    const alreadyProcessing = [];

    try {
      // Generate videos for all selected scripts sequentially
      for (const scriptId of selectedScripts) {
        try {
          const payload = {
            script_id: scriptId,
            ...batchVideoOptions, // Use selected options instead of defaults
          };
          await generateVideo(payload);
          successes.push(scriptId);
        } catch (error) {
          console.error(
            `Error generating video for script ID ${scriptId}:`,
            error
          );

          // Check if error is about already processing/generated videos.
          if (error.isAlreadyProcessing) {
            alreadyProcessing.push(scriptId);
          } else {
            errors.push(scriptId);
          }
        }
      }

      // Close dialog
      setBatchVideoGenDialogOpen(false);

      // Show success/error notifications
      if (successes.length > 0) {
        setNotification({
          open: true,
          message: `Successfully started video generation for ${
            successes.length
          } script${successes.length > 1 ? "s" : ""}`,
          severity: "success",
        });

        // Redirect to videos page after a short delay
        setTimeout(() => {
          navigate("/videos");
        }, 1500);
      }

      if (alreadyProcessing.length > 0) {
        setNotification({
          open: true,
          message: `${alreadyProcessing.length} script${
            alreadyProcessing.length > 1 ? "s" : ""
          } already have videos that are generated or currently processing`,
          severity: "info",
        });
      }

      if (errors.length > 0) {
        setNotification({
          open: true,
          message: `Failed to generate videos for ${errors.length} script${
            errors.length > 1 ? "s" : ""
          }`,
          severity: "error",
        });
      }

      // Clear selections
      setSelectedScripts([]);
    } catch (error) {
      setNotification({
        open: true,
        message: "An error occurred while generating videos",
        severity: "error",
      });
    } finally {
      setBatchVideoGenLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Top section with heading and search */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          mt: "50px",
          padding: "44px",
        }}
      >
        {/* Left side - Title and subtitle */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", fontSize: "25px" }}
          >
            Scripts
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#888", mt: 0.5, textAlign: "left", fontSize: "16px" }}
          >
            All Scripts
          </Typography>
        </Box>

        {/* Right side - Search, sort and buttons */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Search field */}
          <Box sx={{ position: "relative", mr: 2 }}>
            <TextField
              size="small"
              placeholder="Search scripts"
              value={searchText}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "11.31px",
                  color: "#B5B7C0",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ ml: 1, mr: 1, color: "#7E7E7E" }} />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: "pointer", color: "#7E7E7E" }}
                      onClick={handleClearSearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Batch action buttons */}
          <Button
            variant="contained"
            onClick={handleOpenBatchVideoGenDialog}
            disabled={batchVideoGenLoading || selectedScripts.length === 0}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              mr: "18px",
              bgcolor: "#378dea",
              height: "42px",
              minWidth: "160px",
              boxShadow: "none",
              padding: "10px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#1565c0",
              },
              "&:disabled": {
                bgcolor: "#B0C4DE",
                color: "#FFFFFF",
              },
            }}
          >
            {batchVideoGenLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Generate Videos"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleOpenBatchDeleteDialog}
            disabled={selectedScripts.length === 0}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              mr: "18px",
              bgcolor: "#DF0404",
              height: "42px",
              minWidth: "139px",
              boxShadow: "none",
              padding: "10px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
              "&:disabled": {
                bgcolor: "#FFC5C5",
                color: "#FFFFFF",
              },
            }}
          >
            Delete selected
          </Button>

          {/* Sort component */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "18px",
              ml: 1,
            }}
          >
            {/* <SortBy defaultValue="Newest" onSortChange={handleSortChange} /> */}
          </Box>
        </Box>
      </Box>

      {/* Error message if any */}
      {error && (
        <Box sx={{ textAlign: "center", color: "error.main", my: 2 }}>
          <Typography variant="body1">{error}</Typography>
        </Box>
      )}

      {/* Scripts Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          border: "1px solid #eee",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: "#fff", borderBottom: "1px solid #eee" }}
            >
              <TableCell
                padding="checkbox"
                sx={{ borderBottom: "1px solid #eee" }}
              >
                <Checkbox
                  size="small"
                  checked={
                    scripts.length > 0 &&
                    selectedScripts.length === scripts.length
                  }
                  indeterminate={
                    selectedScripts.length > 0 &&
                    selectedScripts.length < scripts.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                ID
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Headline
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && scripts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : scripts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 3 }}>
                  <Typography>No scripts found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              scripts.map((script) => (
                <TableRow
                  key={script.id}
                  sx={{
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    sx={{ borderBottom: "1px solid #eee" }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedScripts.includes(script.id)}
                      onChange={() => handleScriptSelect(script.id)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #eee",
                      color: "#292D32",
                      fontWeight: "bold",
                    }}
                  >
                    {script.id}
                  </TableCell>
                  <TableCell
                    sx={{ borderBottom: "1px solid #eee", color: "#555555" }}
                  >
                    {script.headline}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                    {script.date}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderBottom: "1px solid #eee",
                      whiteSpace: "nowrap",
                      minWidth: "220px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "nowrap",
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          color: "#666",
                          padding: { xs: "6px", sm: "8px" },
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                          },
                          minWidth: { xs: "100%", md: "auto" },
                          width: { xs: "100%", md: "auto" },
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: { xs: "4px", sm: "8px" },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          "&:hover": {
                            bgcolor: "#f5f5f5",
                            border: "1px solid #e0e0e0",
                          },
                        }}
                        onClick={() => handleOpenDetailModal(script)}
                      >
                        <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                          Generate Video
                        </Box>
                        <Box sx={{ display: { xs: "inline", sm: "none" } }}>
                          Video
                        </Box>
                        <MdOutlineSlowMotionVideo
                          style={{
                            fontSize: 18,
                            flexShrink: 0,
                            color: "inherit",
                          }}
                        />
                      </Button>
                      {/* <Button
                        size="small"
                        sx={{
                          textTransform: "none",
                          mr: 1,
                        }}
                        onClick={() => handleCopyLink(script.id)}
                      >
                        <img
                          src={linkIcon}
                          alt="link Icon"
                          style={{
                            width: "44px",
                            height: "33px",
                          }}
                        />
                      </Button> */}
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                          color: "#DF0404",
                          padding: "8px",
                          minWidth: "auto",
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: "#fff1f1",
                            border: "1px solid #DF0404",
                          },
                        }}
                        onClick={() => handleOpenDeleteDialog(script)}
                      >
                        Delete
                        <DeleteIcon
                          sx={{
                            width: 20,
                            height: 20,
                            marginLeft: 0.5,
                          }}
                        />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {scripts.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            Showing data {totalScripts > 0 ? (currentPage - 1) * 24 + 1 : 0} to{" "}
            {Math.min(currentPage * 24, totalScripts)} of {totalScripts} entries
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage === 1 ? "default" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1,
                fontSize: "14px",
              }}
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            >
              &lt;
            </Typography>
            {Array.from({ length: totalPages }).map((_, index) => (
              <Typography
                key={index}
                component="span"
                sx={{
                  mx: 1,
                  bgcolor: currentPage === index + 1 ? "#4691E1" : "#F5F5F5",
                  color: currentPage === index + 1 ? "white" : "#404B52",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </Typography>
            ))}
            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage === totalPages ? "default" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1,
                fontSize: "14px",
              }}
              onClick={() =>
                currentPage < totalPages && setCurrentPage(currentPage + 1)
              }
            >
              &gt;
            </Typography>
          </Box>
        </Box>
      )}

      {/* Detail Modal */}
      <ScriptDetailModal
        open={detailModalOpen}
        handleClose={handleCloseDetailModal}
        scriptId={selectedScriptId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#DF0404" }}>
          Delete Script
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete script "{scriptToDelete?.headline}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
            }}
          >
            {deleteLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Batch Delete Confirmation Dialog */}
      <Dialog
        open={batchDeleteDialogOpen}
        onClose={handleCloseBatchDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#DF0404" }}>
          Delete Selected Scripts
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedScripts.length} selected
            script
            {selectedScripts.length > 1 ? "s" : ""}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseBatchDeleteDialog}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatchDelete}
            disabled={batchDeleteLoading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
            }}
          >
            {batchDeleteLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Video Generation Confirmation Dialog */}
      <Dialog
        open={batchVideoGenDialogOpen}
        onClose={handleCloseBatchVideoGenDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#378DEA" }}>
          Generate Videos
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            You are about to generate videos for {selectedScripts.length}{" "}
            selected script
            {selectedScripts.length > 1 ? "s" : ""}. Please choose the video
            settings below:
          </DialogContentText>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "500" }}>
              Video Settings
            </Typography>

            {/* Avatar Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Avatar:
              </Typography>
              <Select
                size="small"
                fullWidth
                name="avatar_id"
                value={batchVideoOptions.avatar_id}
                onChange={handleBatchVideoOptionChange}
              >
                {AVATARS.map((avatar) => (
                  <MenuItem key={avatar.id} value={avatar.id}>
                    {avatar.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Voice Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Voice:
              </Typography>
              <Select
                size="small"
                fullWidth
                name="voice_id"
                value={batchVideoOptions.voice_id}
                onChange={handleBatchVideoOptionChange}
              >
                {VOICES.map((voice) => (
                  <MenuItem key={voice.id} value={voice.id}>
                    {voice.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Orientation Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Orientation:
              </Typography>
              <Select
                size="small"
                fullWidth
                name="orientation"
                value={batchVideoOptions.orientation}
                onChange={handleBatchVideoOptionChange}
              >
                <MenuItem value="portrait">Portrait</MenuItem>
                <MenuItem value="landscape">Landscape</MenuItem>
              </Select>
            </Box>

            {/* Speed Selection */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Speed:
              </Typography>
              <Select
                size="small"
                fullWidth
                name="speed"
                value={batchVideoOptions.speed}
                onChange={handleBatchVideoOptionChange}
              >
                <MenuItem value={0.8}>Slow (0.8x)</MenuItem>
                <MenuItem value={1.0}>Normal (1.0x)</MenuItem>
                <MenuItem value={1.2}>Fast (1.2x)</MenuItem>
              </Select>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            These settings will be applied to all {selectedScripts.length}{" "}
            selected scripts.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseBatchVideoGenDialog}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatchVideoGen}
            disabled={batchVideoGenLoading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#378DEA",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#1565c0",
              },
            }}
          >
            {batchVideoGenLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Generate Videos"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ScriptsTable;
